import * as React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  functionalUpdate,
  type ColumnDef as TanstackColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { humanizeApiError } from '@discovr/common';
import { Skeleton } from '../../ui/skeleton';
import { Table, TableBody, TableRow, TableCell } from '../../ui/table';
import { Checkbox } from '../../ui/checkbox';
import { QueryErrorState } from '../QueryErrorState';
import { DataTableHeader } from './DataTableHeader';
import { DataTablePagination } from './DataTablePagination';
import type { ColumnDef, DataTableProps } from './types';
import { cn } from '../../../lib/utils';
import { useContainerWidth } from './useContainerWidth';
import { SELECTION_COL_WIDTH } from './constants';
import { toggleRowSelection } from './rowSelection';
import { DataTableRowLabels } from './DataTableRowLabels';
import { skeletonAlignClass, textAlignClass } from './columnAlign';

// Bridge our ColumnDef → a TanStack column; the original rides on meta.layout so
// the renderer keeps its width/align/sticky/wrap. sortDescFirst: right-aligned
// metrics sort desc-first, text asc-first (third-click-clear is TanStack default).
function toTanstackColumns<T>(columns: ColumnDef<T>[]): TanstackColumnDef<T>[] {
  return columns.map((col) => ({
    id: col.id,
    // Unused: cells render via col.cell, and manualSorting means TanStack never
    // reads values to order rows.
    accessorFn: () => null,
    header: () => (typeof col.header === 'function' ? col.header() : col.header),
    cell: (ctx) => col.cell(ctx.row.original),
    enableSorting: !!col.sortable,
    sortDescFirst: col.sortDescFirst ?? col.align === 'right',
    meta: { layout: col },
  }));
}

// Controlled `sort` prop (by:null = unsorted) → TanStack SortingState. Single-column.
function sortPropToState(sort: DataTableProps<unknown>['sort']): SortingState {
  if (!sort || !sort.by) return [];
  return [{ id: sort.by, desc: sort.dir === 'desc' }];
}

// Loading/empty tables reserve a tall block so the page doesn't collapse to a
// short stub and then jolt to full height when rows land — the roster tables
// page 25 rows at a time, so a 5-row skeleton read as a layout shift.
const SKELETON_ROWS = 10;

/**
 * Width applied to every cell of a column (header, data, skeleton, empty).
 * Every column gets a `minWidth` floor (≥ its header) so an empty/loading
 * table reserves the same spacing as a populated one — no cramped empty
 * headers. Non-grow columns then size to their content (so short content
 * doesn't waste space and the table stays as narrow as it needs); grow
 * columns get `width:100%` so they (and they split it when there are several)
 * absorb the leftover space — when the panel narrows they shrink together
 * down to their floor, then the table scrolls.
 *
 * `grow` is ignored when `maxWidth` is also set: in `table-layout: auto`,
 * `width: 100%` on a cell can make browsers disregard that same cell's
 * `max-width` once the table has more room than its columns need — the
 * column balloons past its cap instead of stopping there (observed on an
 * empty/short table with few columns). A capped column is bounded by
 * definition, so it shouldn't also be a growth target.
 */
// Extra column width reserved for a sortable header's sort caret (the icon +
// its gap, ~16px, rounded up for breathing room). Under table-layout: fixed the
// column width is exact, so a minWidth sized for the label alone would clip the
// caret past the column border — reserving it column-wide (header shares the
// width with the body) keeps the caret fully visible without per-column tuning.
// Kept in step with the caret size in DataTableHeader.tsx.
//
// This allowance is USELESS on its own if a column also sets an explicit
// maxWidth tighter than minWidth+this — CSS max-width clamps the computed
// width straight back down, silently eating the reserved caret space (found
// live: the sort caret visually disappeared on any sortable column whose
// maxWidth was set before sorting existed, or tuned tight around other header
// content like a tooltip icon). colWidthStyle below guards against this by
// never letting maxWidth clamp below fixedColWidth(col).
const SORT_CARET_ALLOWANCE_PX = 20;

/** The fixed pixel width a column occupies: its minWidth plus the sort-caret
 *  allowance when the column is sortable. Grow columns use this only as a floor. */
function fixedColWidth<T>(col: ColumnDef<T>): number {
  return col.minWidth + (col.sortable ? SORT_CARET_ALLOWANCE_PX : 0);
}

/** Where a left-pinned column sticks: after the selection column and any
 *  left-pinned column before it, so several pinned columns sit side by side. */
export function stickyLeftOffset<T>(
  columns: ColumnDef<T>[],
  col: ColumnDef<T>,
  selectionWidth: number,
): number {
  let left = selectionWidth;
  for (const c of columns) {
    if (c === col || c.id === col.id) break;
    if (c.sticky === 'left') left += fixedColWidth(c);
  }
  return left;
}

export function colWidthStyle<T>(col: ColumnDef<T>): React.CSSProperties {
  // table-layout: fixed sizes columns off their `width` and ignores content, so
  // sorting/paging can never reflow them. Each column gets a deterministic width:
  //  - bounded-grow (grow, no cap) claims the leftover via width:100%;
  //  - capped-grow (grow + maxWidth) pins to its cap, so it still reaches its
  //    intended max width (min-width/max-width are ignored for sizing under
  //    fixed layout, so the cap has to ride on `width`);
  //  - every other column's width IS its minWidth floor (+ caret allowance) — so
  //    minWidth is not set redundantly alongside it.
  if (col.grow && col.maxWidth == null) {
    return { minWidth: col.minWidth, width: '100%' };
  }
  if (col.grow) {
    // Never let an explicit cap clamp below what this column actually needs
    // (fixedColWidth already includes the caret allowance for a sortable
    // column) — a maxWidth tuned before sorting existed on this column can't
    // squeeze the caret out of the cell.
    const w = Math.max(col.maxWidth as number, fixedColWidth(col));
    return { minWidth: col.minWidth, width: w, maxWidth: w };
  }
  const base: React.CSSProperties = { width: fixedColWidth(col) };
  if (col.maxWidth != null) base.maxWidth = Math.max(col.maxWidth, fixedColWidth(col));
  return base;
}

/**
 * True when every column is capped (fixed or bounded-grow) — i.e. none of
 * them claims the leftover table width. The base `Table` primitive is
 * `w-full`, so `table-layout: auto` then proportionally stretches EVERY
 * column (even capped ones) to fill that leftover space instead of leaving
 * it unused. When this is true, `DataTable`/`DataTableHeader` add an
 * invisible trailing spacer column so real columns hold their widths.
 */
export function needsTrailingSpacer<T>(columns: ColumnDef<T>[]): boolean {
  return !columns.some((col) => col.grow && col.maxWidth == null);
}

/**
 * Horizontal cell padding = the inter-column gap, staged by CONTAINER width so
 * the whole table condenses together as side panels open (not just the grow
 * column). The wrapper is a `@container/dt`, so these breakpoints react to the
 * table's own available width, which is exactly what shrinks when a sidebar
 * opens: both panels open (~narrow) → 8px, one panel open (~medium) → 12px,
 * no panels (~wide) → 16px. Kept identical on header + every cell type and
 * shared app-wide via this component. (Header file inlines the same string.)
 *
 * This is the fallback/default for tables with a genuine grow column. Tables
 * where every column is fixed instead measure their real leftover space and
 * distribute it as an exact per-boundary gap — see `useDistributedGap` below.
 */
export const CELL_X = 'px-2 @4xl/dt:px-3 @6xl/dt:px-4';

// Baseline per-side padding used once a table switches to measured gap
// distribution — matches CELL_X's widest tier (px-4). Distribution only
// activates when there's leftover space to begin with, so the narrow-tier
// compression CELL_X does under tight space is never needed here.
const GAP_BASE_PX = 16;

/**
 * When every column in a table is fixed-width (no column claims the leftover
 * space via `grow`), measure the table's real available width and spread
 * whatever's left over EVENLY across every inter-column boundary — e.g. 5
 * columns leaving 100px unused → 25px extra gap at each of the 4 boundaries
 * between them. Falls back to `null` (caller uses the static CELL_X classes
 * + trailing spacer instead) until the first measurement lands, and whenever
 * there's no leftover to distribute (table needs to scroll instead).
 */
function useDistributedGap<T>(
  columns: ColumnDef<T>[],
  computedMinWidth: number,
): { ref: React.RefObject<HTMLDivElement | null>; perBoundaryPx: number | null } {
  const [ref, measuredWidth] = useContainerWidth<HTMLDivElement>();
  const eligible = needsTrailingSpacer(columns) && columns.length > 1;
  if (!eligible || measuredWidth == null) return { ref, perBoundaryPx: null };
  const leftover = measuredWidth - computedMinWidth;
  if (leftover <= 0) return { ref, perBoundaryPx: null };
  return { ref, perBoundaryPx: leftover / (columns.length - 1) };
}

/** Per-cell inline padding for measured gap-distribution mode — `null` when
 *  inactive, letting the CELL_X className apply normally instead. */
export function distributedPaddingStyle(
  perBoundaryPx: number | null,
  index: number,
  colCount: number,
): React.CSSProperties | undefined {
  if (perBoundaryPx == null) return undefined;
  const half = perBoundaryPx / 2;
  return {
    paddingLeft: GAP_BASE_PX + (index === 0 ? 0 : half),
    paddingRight: GAP_BASE_PX + (index === colCount - 1 ? 0 : half),
  };
}

export function DataTable<T>({
  ariaLabel,
  columns,
  data,
  getRowId,
  loading = false,
  error,
  onRetry,
  retrying,
  emptyState,
  onRowClick,
  rowAriaLabel,
  pagination,
  sort,
  minWidth: minWidthProp,
  className,
  fillHeight = false,
  autoHeight = false,
  rowSelection,
  hideHeader = false,
  rowClassName,
}: DataTableProps<T>): React.ReactElement {
  const colCount = columns.length;
  // Real columns + the selection column (if any) — used only for colSpan on
  // full-width rows (error/skeleton spacer).
  const totalColCount = colCount + (rowSelection ? 1 : 0);
  // The selection column is fixed-width but DOES consume real measured
  // container width, unlike the trailing spacer (which self-corrects by
  // absorbing whatever's left). It must be folded into computedMinWidth so
  // useDistributedGap's leftover math doesn't over-allocate padding by
  // SELECTION_COL_WIDTH and push the table past its container.
  const selectionWidth = rowSelection ? SELECTION_COL_WIDTH : 0;
  // Sum the columns' actual fixed widths (minWidth + sort-caret allowance) so the
  // table's min-width matches what the columns really occupy — otherwise the
  // reserved caret space would push the table past its container unaccounted for.
  const computedMinWidth =
    minWidthProp ?? columns.reduce((sum, col) => sum + fixedColWidth(col), selectionWidth);
  const isEmpty = !loading && !error && data.length === 0;
  const { ref: measureRef, perBoundaryPx } = useDistributedGap(columns, computedMinWidth);
  // Trailing spacer is the fallback: only rendered while gap-distribution
  // hasn't measured yet (or has nothing to distribute), so there's no
  // unstyled flash before the real per-boundary gaps take over.
  const showSpacer = needsTrailingSpacer(columns) && perBoundaryPx == null;

  const tsColumns = React.useMemo(() => toTanstackColumns(columns), [columns]);
  // Ids come from the row's position, never from the row, so no personal data
  // ends up in an attribute.
  const labelIdBase = React.useId();
  const selectLabelId = (rowIndex: number) => `${labelIdBase}-select-${rowIndex}`;
  const selectLabel = rowSelection
    ? (row: T, rowId: string) => `Select ${rowSelection.getRowLabel?.(row) ?? rowId}`
    : undefined;
  const sorting = React.useMemo(
    () => sortPropToState(sort as DataTableProps<unknown>['sort']),
    [sort],
  );
  // TanStack owns the row model + sorting state; manual* means rows arrive
  // already sorted/paged from the server (it never reorders). Selection stays
  // prop-driven below (cross-page-safe).
  const table = useReactTable<T>({
    data,
    columns: tsColumns,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    enableMultiSort: false,
    state: { sorting },
    onSortingChange: (updater) => {
      if (!sort) return;
      const first = functionalUpdate(updater, sorting)[0];
      sort.onChange(
        first ? { by: first.id, dir: first.desc ? 'desc' : 'asc' } : { by: null, dir: 'asc' },
      );
    },
  });

  return (
    <div
      className={cn(
        '@container/dt overflow-hidden rounded-xl border border-border bg-card',
        // Sticky header (industry-standard for wide data tables): table.tsx's
        // table-container → table → {thead, tbody} cascade always scrolls
        // ONLY tbody, below the (never-scrolling) header — see the comment on
        // Table in table.tsx. All this className controls is how tall
        // table-container itself gets to be, which that cascade then fills.
        fillHeight
          // Fill mode (deprecated — migrating to the viewport-cap mode): card
          // is a flex column that hugs its content but shrinks within a bounded
          // flex parent; table-container grows to take whatever height remains.
          ? 'flex min-h-0 flex-col **:data-[slot=table-container]:min-h-0 **:data-[slot=table-container]:flex-1'
          // Viewport-cap mode (default): the card hugs its content, but the
          // scroll area caps at the height left in the viewport below the
          // pinned filters, then scrolls internally with the header pinned. The
          // cap subtracts the sticky stack above the table (`--dt-top-offset`,
          // published by StickyTableToolbar; falls back to 13rem) and the
          // pagination footer (`--dt-pager-h`) so the pager stays visible.
          : autoHeight
            // Content-height mode: no cap at all, so the page's own scroll is
            // the only one. For short report tables inside a card.
            ? undefined
            : '**:data-[slot=table-container]:max-h-[calc(100dvh-var(--dt-top-offset,13rem)-var(--dt-pager-h,0))]',
        // Keyboard focus on a cell scrolls clear of a pinned-right column.
        '**:data-[slot=table-container]:[scroll-padding-right:var(--dt-sticky-right-w,0px)]',
        className,
      )}
      style={{
        '--dt-pager-h': pagination ? '52px' : '0px',
        '--dt-sticky-right-w': `${columns.filter((c) => c.sticky === 'right').reduce((w, c) => w + fixedColWidth(c), 0)}px`,
        // Deliberately UNCAPPED, even for a table with no bounded-grow column:
        // useDistributedGap below measures the real leftover space (panel width
        // minus the columns' own fixed widths) and spreads it as extra
        // inter-column padding, so the table visually stretches to fill its
        // panel — e.g. hiding columns via the Columns menu widens the gaps
        // between what's left, instead of shrinking the card and leaving a
        // blank strip beside it. Falls back to the trailing spacer (still
        // rendered below) for the one case gap-distribution can't help with —
        // a single remaining column, with no interior boundary to widen.
      } as React.CSSProperties}
    >
      {/* relative wrapper so the empty-state overlay sits on top — also the
          measurement target for gap-distribution (same width as the table's
          own scroll container, which the shared Table primitive doesn't
          expose a ref for). */}
      <div
        ref={measureRef}
        className={cn('relative', fillHeight && 'flex min-h-0 flex-1 flex-col')}
        style={{ minHeight: isEmpty || loading ? 560 : undefined }}
      >
        {/* border-separate/border-spacing-0 and table-layout:fixed (pins
         * column widths to `width` from colWidthStyle instead of re-fitting
         * to the widest cell, so a sort/page swap can't jolt columns
         * sideways) both live on TableRow now, not here — see table.tsx. */}
        <Table aria-label={ariaLabel} style={{ minWidth: computedMinWidth }}>
          <DataTableHeader
            table={table}
            columns={columns}
            perBoundaryPx={perBoundaryPx}
            rowSelection={rowSelection}
            data={data}
            getRowId={getRowId}
            visuallyHidden={hideHeader}
          />

          <TableBody>
            {loading ? (
              <SkeletonRows count={SKELETON_ROWS} columns={columns} showSpacer={showSpacer} perBoundaryPx={perBoundaryPx} hasSelectionCol={!!rowSelection} />
            ) : error ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={showSpacer ? totalColCount + 1 : totalColCount} className="p-0">
                  <QueryErrorState
                    message={humanizeApiError(error)}
                    onRetry={onRetry}
                    retrying={retrying}
                  />
                </TableCell>
              </TableRow>
            ) : isEmpty ? (
              // Empty (done loading, no rows): render NO body rows — just the
              // centered text overlay below. The skeleton (grey bars) is for the
              // loading state only; a fetched-but-empty table shows text alone.
              null
            ) : (
              table.getRowModel().rows.map((tsRow, rowIndex) => {
                const rowId = tsRow.id;
                const row = tsRow.original;
                // The last row omits its bottom border so the pagination's
                // top border is the single divider between rows and paginator
                // (no doubled line). With no paginator, the card's own border
                // closes the table.
                const isLastRow = rowIndex === data.length - 1;
                return (
                  <TableRow
                    key={rowId}
                    className={cn(
                      // `group` lets cells use `group-hover:` (e.g. row-hover
                      // reveal of an action button) — preserved from the
                      // bespoke roster tables.
                      'group bg-card',
                      onRowClick
                        ? 'cursor-pointer hover:bg-accent'
                        : 'hover:bg-transparent',
                      rowClassName,
                    )}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    role={onRowClick ? 'button' : undefined}
                    aria-label={onRowClick && rowAriaLabel ? rowAriaLabel(row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            // Rows can contain their own interactive elements
                            // (e.g. an Actions column of icon buttons, or a
                            // selection checkbox) — each is its own tab stop
                            // and handles its own Enter/Space natively. Only
                            // react here when the ROW ITSELF is the key
                            // target (i.e. it was Tab-focused directly),
                            // otherwise a keypress on a nested button would
                            // bubble up and fire the row action too.
                            if (e.target !== e.currentTarget) return;
                            if (e.key === 'Enter') {
                              onRowClick(row);
                            } else if (e.key === ' ') {
                              // Prevent the page from scrolling on Space.
                              e.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                  >
                    {rowSelection && (
                      <TableCell
                        // Sticky left:0, same as the first data column below —
                        // without this the checkbox column scrolls away on its
                        // own while the data column pins in place, so a
                        // horizontal scroll drags the checkbox out from under
                        // an otherwise-still row (and the un-pinned column
                        // scrolling underneath a merely-visual pin can flash
                        // through before the sticky column's opaque
                        // background catches up). data-sticky-left opts this
                        // cell into the same JS scroll compensation as the
                        // data column — see the comment on it there.
                        data-sticky-left=""
                        style={{ width: SELECTION_COL_WIDTH }}
                        className={cn(
                          'sticky left-0 z-10 bg-card px-2',
                          !isLastRow && 'border-b border-border',
                        )}
                        // Selecting a row must never also fire onRowClick.
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(rowSelection.isRowSelectable?.(row) ?? true) && (
                          <Checkbox
                            aria-labelledby={selectLabelId(rowIndex)}
                            checked={rowSelection.selectedIds.has(rowId)}
                            onCheckedChange={() =>
                              rowSelection.onSelectionChange(toggleRowSelection(rowSelection.selectedIds, rowId))
                            }
                          />
                        )}
                      </TableCell>
                    )}
                    {tsRow.getVisibleCells().map((cell, index) => {
                      const col = cell.column.columnDef.meta?.layout;
                      if (!col) return null;
                      return (
                        <TableCell
                          key={cell.id}
                          // Marks this cell for TableBody's scroll-compensation
                          // effect — see the comment on data-sticky-left there
                          // for why CSS position:sticky alone isn't enough here.
                          data-sticky-left={col.sticky === 'left' ? '' : undefined}
                          data-sticky-right={col.sticky === 'right' ? '' : undefined}
                          style={{
                            ...colWidthStyle(col),
                            ...distributedPaddingStyle(perBoundaryPx, index, colCount),
                            // Sits right after the sticky checkbox column
                            // (0px when there's no selection column) —
                            // otherwise they'd both pin to the container's
                            // left:0 and overlap instead of sitting side by
                            // side.
                            ...(col.sticky === 'left'
                              ? { left: stickyLeftOffset(columns, col, selectionWidth) }
                              : {}),
                          }}
                          whitespace={col.wrap ? 'normal' : 'nowrap'}
                          className={cn(
                            // Inter-column gap is staged by container width (CELL_X)
                            // so the whole table condenses as side panels open —
                            // overridden by inline padding above once gap
                            // distribution has a real measurement.
                            'py-4',
                            !isLastRow && 'border-b border-border',
                            CELL_X,
                            textAlignClass(col),
                            // z-10: without it, later-in-DOM scrolling cells (e.g. a
                            // right-hand Actions column) paint over this pinned cell
                            // at the same implicit stacking level as it scrolls
                            // underneath, instead of sliding cleanly beneath it.
                            col.sticky === 'left' && 'sticky z-10 bg-card',
                            col.sticky === 'right' && 'sticky right-0 z-10 bg-card in-data-[right-overlap]:shadow-[inset_1px_0_0_var(--border)]',
                            col.cellClassName,
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                    {showSpacer && (
                      <TableCell
                        aria-hidden
                        style={{ width: '100%' }}
                        className={cn(!isLastRow && 'border-b border-border')}
                      />
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <DataTableRowLabels
          rows={table.getRowModel().rows}
          selectLabelId={selectLabelId}
          selectLabel={selectLabel}
        />

        {/* Empty-state overlay */}
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4">
            <div className="pointer-events-auto">
              {emptyState ?? <p className="text-body-sm-medium text-muted-foreground">No records found.</p>}
            </div>
          </div>
        )}
      </div>

      {/* Pagination footer rendered outside the relative div so it's always below */}
      {pagination && <DataTablePagination pagination={pagination} />}
    </div>
  );
}

// Skeleton + empty rows render ONE cell per real column with that column's
// width — so the header/column spacing is byte-identical whether the table is
// loading, empty, or populated (the whole point: no "cramped empty table").
function SkeletonRows<T>({
  count,
  columns,
  showSpacer,
  perBoundaryPx,
  hasSelectionCol,
}: {
  count: number;
  columns: ColumnDef<T>[];
  showSpacer: boolean;
  perBoundaryPx: number | null;
  hasSelectionCol: boolean;
}): React.ReactElement {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <TableRow key={i} className="hover:bg-transparent">
          {hasSelectionCol && (
            <TableCell
              style={{ width: SELECTION_COL_WIDTH }}
              className={cn('px-2', i !== count - 1 && 'border-b border-border')}
            />
          )}
          {columns.map((col, j) => (
            <TableCell key={col.id} style={{ ...colWidthStyle(col), ...distributedPaddingStyle(perBoundaryPx, j, columns.length) }} className={cn('py-4', i !== count - 1 && 'border-b border-border', CELL_X)}>
              {j === 0 ? (
                <div className="flex items-center gap-3">
                  <Skeleton className="size-8 shrink-0 rounded-full" />
                  <Skeleton className="h-3 w-24" />
                </div>
              ) : col.skeletonShape === 'pill' || col.id === 'status' ? (
                // Any column whose real cell renders a rounded-full badge
                // (StatusBadge, deliverable-type chips, etc.) opts in via
                // `skeletonShape: 'pill'` — status columns get it for free
                // by id since every "status" column across the app is a pill.
                <Skeleton className={cn('h-6 w-20 rounded-full', skeletonAlignClass(col))} />
              ) : (
                <Skeleton className={cn('h-3 w-16', skeletonAlignClass(col))} />
              )}
            </TableCell>
          ))}
          {showSpacer && (
            <TableCell aria-hidden style={{ width: '100%' }} className={cn(i !== count - 1 && 'border-b border-border')} />
          )}
        </TableRow>
      ))}
    </>
  );
}
